/**
 * AuditModal Component
 *
 * Modal dialog for displaying detailed audit information (coding or quality audits).
 * Shows audit metadata with Note ID + inline ChartPath icon, compact Q&A grid,
 * and score with color-coded badge showing percentage.
 *
 * Badge color coding:
 * - Score >= 89: lightSuccess (green)
 * - Score 70-88: lightWarning (yellow)
 * - Score < 70: lightError (red)
 */

import { ExternalLink, Mail, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { askQuestion } from '@/services/askQuestionService'
import { useAuditDetail } from '@/hooks/equip/useEquipDashboard'

interface AuditModalProps {
  auditId: number | null
  auditType: string
  onClose: () => void
  providerName?: string
  providerEmail?: string
  year?: number
  quarter?: number
}

export function AuditModal({
  auditId,
  auditType,
  onClose,
  providerName,
  providerEmail,
  year,
  quarter
}: AuditModalProps) {
  const auditQuery = useAuditDetail(auditId, auditType)
  const auditData = auditQuery.data ?? null
  const isLoading = auditQuery.isPending
  const error = auditQuery.error?.message ?? null


  // Determine badge variant based on score
  const getScoreBadgeVariant = (score: number) => {
    if (score >= 89) return 'lightSuccess'
    if (score >= 70) return 'lightWarning'
    return 'lightError'
  }

  // Format date for display
  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  // Build ChartPath URL based on audit type
  const getChartPathUrl = () => {
    if (!auditData || !auditData.chartId) return null

    // Coding audits link to note detail, quality audits might link differently
    if (auditType === 'coding') {
      return `https://chartpath.eventuswh.com/note/${auditData.chartId}`
    } else if (auditType === 'quality') {
      return `https://chartpath.eventuswh.com/note/${auditData.chartId}`
    }
    return null
  }

  return (
    <Dialog open={auditId !== null} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl min-h-[70vh] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isLoading ? (
              <Skeleton className="h-8 w-64" />
            ) : auditData ? (
              <div>
                <div className="text-2xl font-semibold">
                  {auditType === 'coding' ? 'Coding Audit' : 'Signature Standards Audit'}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {auditData.auditDate ? formatDate(auditData.auditDate) : '-'}
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground">Audit not found</div>
            )}
          </DialogTitle>
        </DialogHeader>

          <div>
            {isLoading && (
              <div className="space-y-3 mt-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 p-4 text-sm text-destructive bg-destructive/10 rounded-md mt-3">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {!isLoading && auditData && (
              <div className="space-y-3 mt-3">
            {/* Metadata Section */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              {auditData.chartId && (
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Note ID</span>
                  <Badge variant="outline" className="font-medium">
                    {auditData.chartId}
                    {getChartPathUrl() && (
                      <a
                        href={getChartPathUrl()!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 transition-colors ml-1"
                        title="Open in ChartPath"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </Badge>
                </div>
              )}
              {auditData.auditor && (
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Auditor</span>
                  <Badge variant="outline" className="font-medium">{auditData.auditor}</Badge>
                </div>
              )}
              {auditData.score !== null && auditData.score !== undefined && (
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Score</span>
                  <Badge variant={getScoreBadgeVariant(auditData.percentage ?? 0) as any} className="font-medium">
                    {auditData.maxScore != null && auditData.maxScore > 0
                      ? `${auditData.score}/${auditData.maxScore} (${Math.round((auditData.score / auditData.maxScore) * 100)}%)`
                      : `${auditData.percentage != null ? `${auditData.percentage.toFixed(0)}%` : auditData.score}`
                    }
                  </Badge>
                </div>
              )}
              {auditData.notes && (
                <div className="flex items-center gap-1.5 basis-full">
                  <span className="text-muted-foreground">Notes</span>
                  <Badge variant="outline" className="font-medium">{auditData.notes}</Badge>
                </div>
              )}
            </div>

            {/* Questions Section - Compact Table Grid */}
            {auditData.questions && auditData.questions.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2">Audit Questions</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-4 text-xs font-semibold uppercase text-muted-foreground">Question</th>
                      <th className="text-left py-2 pr-4 text-xs font-semibold uppercase text-muted-foreground">Response</th>
                      <th className="text-right py-2 text-xs font-semibold uppercase text-muted-foreground">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditData.questions.map((question, index) => {
                      const hasScore = question.pointsEarned != null || question.pointsPossible != null
                      const earned = question.pointsEarned ?? 0
                      // If points_possible is missing but points_earned exists, infer max = earned
                      const possible = (question.pointsPossible != null && question.pointsPossible > 0)
                        ? question.pointsPossible
                        : earned > 0 ? earned : 0
                      const scoreBadgeVariant =
                        possible > 0 && earned >= possible
                          ? 'lightSuccess'
                          : earned === 0 && possible > 0
                            ? 'lightError'
                            : 'lightWarning'

                      return (
                        <tr key={index} className="border-b border-border/50 last:border-0">
                          <td className="py-2 pr-4">{question.questionText}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{question.response?.replace(/^"|"$/g, '') ?? '\u2014'}</td>
                          <td className="py-2 text-right whitespace-nowrap">
                            {hasScore ? (
                              <Badge variant={scoreBadgeVariant as any}>
                                {earned}/{possible}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">{'\u2014'}</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  </table>
                </div>
              </div>
            )}
              </div>
            )}
          </div>

        {/* Footer */}
        <div className="mt-4 pt-4 border-t">
          <Button
            variant="outline"
            onClick={async () => {
              if (!auditData || auditId === null) return
              await askQuestion({
                type: 'audit',
                identifier: String(auditId),
                providerName: providerName || 'Unknown',
                providerEmail: providerEmail || '',
                year: year || new Date().getFullYear(),
                quarter: quarter || Math.ceil((new Date().getMonth() + 1) / 3),
              })
            }}
          >
            <Mail className="mr-2 h-4 w-4" />
            Ask a Question
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
